/**
 * PayslipGenerationProgress Component
 * Shows real-time progress of async payslip generation
 * Can be minimized to bottom corner while user continues working
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
    X,
    Minimize2,
    Maximize2,
    CheckCircle,
    XCircle,
    Loader,
    FileText,
    AlertTriangle
} from 'lucide-react';
import payrollService from '../../../../services/payroll.service';

const PayslipGenerationProgress = ({
    jobId,
    onComplete,
    onCancel,
    onClose
}) => {
    const [job, setJob] = useState(null);
    const [polling, setPolling] = useState(true);
    const [minimized, setMinimized] = useState(false);
    const [error, setError] = useState(null);
    const completedRef = React.useRef(false); // Track if onComplete was already called

    // Reset completed ref when jobId changes
    React.useEffect(() => {
        completedRef.current = false;
    }, [jobId]);

    // Poll for progress
    const pollProgress = useCallback(async () => {
        if (!jobId || !polling) return;

        try {
            const result = await payrollService.getPayslipJobProgress(jobId);

            if (result.success && result.job) {
                setJob(result.job);
                setError(null);

                // Stop polling when job is done
                if (result.job.status === 'completed' ||
                    result.job.status === 'failed' ||
                    result.job.status === 'cancelled') {
                    setPolling(false);

                    // Only call onComplete once
                    if (result.job.status === 'completed' && !completedRef.current) {
                        completedRef.current = true;
                        onComplete?.(result.job);
                    }
                }
            }
        } catch (err) {
            console.error('Error polling job progress:', err);
            setError('Failed to get progress');
        }
    }, [jobId, polling, onComplete]);

    // Start polling on mount
    useEffect(() => {
        if (!jobId) return;

        // Initial poll
        pollProgress();

        // Poll every 1 second
        const interval = setInterval(pollProgress, 1000);
        return () => clearInterval(interval);
    }, [jobId, pollProgress]);

    // Handle cancel
    const handleCancel = async () => {
        try {
            await payrollService.cancelPayslipJob(jobId);
            onCancel?.(jobId);
        } catch (err) {
            console.error('Error cancelling job:', err);
            setError('Failed to cancel job');
        }
    };

    // Get status icon and color
    const getStatusDisplay = () => {
        if (!job) return { icon: <Loader className="animate-spin" size={20} />, color: 'text-blue-600', bg: 'bg-blue-50' };

        switch (job.status) {
            case 'completed':
                return { icon: <CheckCircle size={20} />, color: 'text-green-600', bg: 'bg-green-50' };
            case 'failed':
                return { icon: <XCircle size={20} />, color: 'text-red-600', bg: 'bg-red-50' };
            case 'cancelled':
                return { icon: <XCircle size={20} />, color: 'text-gray-600', bg: 'bg-gray-50' };
            case 'processing':
                return { icon: <Loader className="animate-spin" size={20} />, color: 'text-blue-600', bg: 'bg-blue-50' };
            default:
                return { icon: <FileText size={20} />, color: 'text-gray-600', bg: 'bg-gray-50' };
        }
    };

    const { icon, color, bg } = getStatusDisplay();

    // Minimized view
    if (minimized) {
        return (
            <div
                className={`fixed bottom-4 right-4 ${bg} rounded-full shadow-lg border p-3 cursor-pointer flex items-center gap-2 z-50 hover:scale-105 transition-transform`}
                onClick={() => setMinimized(false)}
            >
                <span className={color}>{icon}</span>
                {job && job.status === 'processing' && (
                    <span className="text-sm font-medium text-gray-700">
                        {job.progress}%
                    </span>
                )}
                {job && job.status === 'completed' && (
                    <span className="text-sm font-medium text-green-700">
                        Done
                    </span>
                )}
            </div>
        );
    }

    return (
        <div className="fixed bottom-4 right-4 w-96 bg-white rounded-lg shadow-xl border z-50">
            {/* Header */}
            <div className={`flex justify-between items-center px-4 py-3 border-b ${bg} rounded-t-lg`}>
                <div className="flex items-center gap-2">
                    <span className={color}>{icon}</span>
                    <h4 className="font-semibold text-gray-800">
                        {job?.status === 'completed' ? 'Payslips Generated' :
                         job?.status === 'failed' ? 'Generation Failed' :
                         job?.status === 'cancelled' ? 'Generation Cancelled' :
                         'Generating Payslips'}
                    </h4>
                </div>
                <div className="flex gap-1">
                    <button
                        onClick={() => setMinimized(true)}
                        className="p-1 hover:bg-gray-200 rounded transition-colors"
                        title="Minimize"
                    >
                        <Minimize2 size={16} className="text-gray-500" />
                    </button>
                    {(job?.status === 'completed' || job?.status === 'failed' || job?.status === 'cancelled') && (
                        <button
                            onClick={onClose}
                            className="p-1 hover:bg-gray-200 rounded transition-colors"
                            title="Close"
                        >
                            <X size={16} className="text-gray-500" />
                        </button>
                    )}
                </div>
            </div>

            {/* Body */}
            <div className="p-4">
                {error && (
                    <div className="mb-3 p-2 bg-red-50 text-red-700 text-sm rounded flex items-center gap-2">
                        <AlertTriangle size={16} />
                        {error}
                    </div>
                )}

                {/* Progress Bar */}
                {job && (
                    <>
                        <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                            <div
                                className={`h-3 rounded-full transition-all duration-300 ${
                                    job.status === 'completed' ? 'bg-green-500' :
                                    job.status === 'failed' ? 'bg-red-500' :
                                    'bg-blue-600'
                                }`}
                                style={{ width: `${job.progress || 0}%` }}
                            />
                        </div>

                        {/* Status Text */}
                        <div className="text-sm text-gray-600 mb-2">
                            {job.processedCount || 0} / {job.totalCount || 0} employees
                            <span className="float-right font-medium">{job.progress || 0}%</span>
                        </div>

                        {/* Current Employee (only when processing) */}
                        {job.status === 'processing' && job.currentEmployeeName && (
                            <div className="text-xs text-gray-500 mb-2 truncate">
                                {job.currentStep}: <span className="font-medium">{job.currentEmployeeName}</span>
                            </div>
                        )}

                        {/* ETA (only when processing) */}
                        {job.status === 'processing' && job.etaSeconds > 0 && (
                            <div className="text-xs text-gray-400 mb-3">
                                Estimated time: ~{job.etaSeconds < 60
                                    ? `${job.etaSeconds} seconds`
                                    : `${Math.ceil(job.etaSeconds / 60)} minute${Math.ceil(job.etaSeconds / 60) > 1 ? 's' : ''}`
                                } remaining
                            </div>
                        )}

                        {/* Completion Status */}
                        {job.status === 'completed' && (
                            <div className="mt-3 p-3 bg-green-50 rounded-lg">
                                <div className="flex items-center gap-2 text-green-700 font-medium">
                                    <CheckCircle size={18} />
                                    {job.totalCount - job.failedCount} payslips generated successfully
                                </div>
                                {job.failedCount > 0 && (
                                    <div className="mt-1 text-sm text-orange-600">
                                        {job.failedCount} failed - check errors below
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Failed Status */}
                        {job.status === 'failed' && (
                            <div className="mt-3 p-3 bg-red-50 rounded-lg">
                                <div className="flex items-center gap-2 text-red-700 font-medium">
                                    <XCircle size={18} />
                                    Generation failed
                                </div>
                                <div className="mt-1 text-sm text-red-600">
                                    {job.processedCount} of {job.totalCount} completed before failure
                                </div>
                            </div>
                        )}

                        {/* Error Details (if any) */}
                        {job.errors && job.errors.length > 0 && (
                            <div className="mt-3">
                                <div className="text-xs font-medium text-gray-500 mb-1">
                                    Errors ({job.errors.length}):
                                </div>
                                <div className="max-h-24 overflow-y-auto text-xs bg-red-50 rounded p-2">
                                    {job.errors.slice(0, 5).map((err, idx) => (
                                        <div key={idx} className="text-red-600 mb-1">
                                            {err.employeeName}: {err.error}
                                        </div>
                                    ))}
                                    {job.errors.length > 5 && (
                                        <div className="text-gray-500 mt-1">
                                            ...and {job.errors.length - 5} more
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* Loading state */}
                {!job && !error && (
                    <div className="flex items-center justify-center py-4">
                        <Loader className="animate-spin text-blue-600" size={24} />
                        <span className="ml-2 text-gray-600">Starting...</span>
                    </div>
                )}
            </div>

            {/* Footer - Cancel button (only when processing) */}
            {job && (job.status === 'pending' || job.status === 'processing') && (
                <div className="px-4 pb-4">
                    <button
                        onClick={handleCancel}
                        className="w-full py-2 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors text-sm font-medium"
                    >
                        Cancel Generation
                    </button>
                </div>
            )}

            {/* Footer - Close button (when done) */}
            {job && (job.status === 'completed' || job.status === 'failed' || job.status === 'cancelled') && (
                <div className="px-4 pb-4">
                    <button
                        onClick={onClose}
                        className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium"
                    >
                        Close
                    </button>
                </div>
            )}
        </div>
    );
};

export default PayslipGenerationProgress;
